<template>
  <v-container fluid class="ma-0 pa-0">
    <v-progress-linear v-if="caricamento" indeterminate color="primary" />

    <v-alert v-if="dati && !resaAttendibile" type="warning" dense text class="mx-2 mt-2 mb-0">
      Con il filtro operatore attivo le ore sono quelle dell'operatore selezionato, mentre fatturato e resa non vengono
      mostrati: il fatturato appartiene al cliente e non è attribuibile al singolo operatore.
    </v-alert>

    <div v-if="dati" class="text-caption text--secondary px-3 pt-2">
      {{ periodoCorrente }} a confronto con {{ periodoPrecedente }}<span v-if="dati.parziale"> (stesso periodo)</span>.
      Sono incluse tutte le ore dedicate ai clienti, fatturabili e non, a prescindere dall'opzione «Solo ore da
      fatturare». Resa = fatturato / ore dedicate.
    </div>

    <!-- Indicatori -->
    <v-row class="ma-0 pa-2">
      <v-col cols="12" sm="6" md="4" lg="2" v-for="kpi in schede" :key="kpi.etichetta">
        <v-card class="elevation-2 fill-height">
          <v-card-text class="pa-3">
            <div class="text-caption text--secondary">{{ kpi.etichetta }}</div>
            <div class="text-h6 font-weight-bold">{{ kpi.valore }}</div>
            <div v-if="'variazione' in kpi" class="text-caption d-flex align-center"
              :class="classeVariazione(kpi.variazione)">
              <v-icon x-small class="mr-1" :class="classeVariazione(kpi.variazione)">
                {{ iconaVariazione(kpi.variazione) }}
              </v-icon>
              {{ fmtPerc(kpi.variazione) }} vs {{ annoPrec }}
            </div>
            <div v-else class="text-caption text--secondary">{{ kpi.nota }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0">
      <!-- Ore dedicate e fatturato per cliente -->
      <v-col cols="12" md="7">
        <v-card class="elevation-2">
          <v-card-title>
            Ore dedicate e fatturato per cliente
            <v-tooltip bottom max-width="320">
              <template v-slot:activator="{ on }">
                <v-icon small class="ml-2" v-on="on">info</v-icon>
              </template>
              <span>Ogni punto è un cliente. Sopra la linea tratteggiata la resa è superiore alla media, sotto è
                inferiore. Clicca su un punto per vedere il dettaglio.</span>
            </v-tooltip>
          </v-card-title>
          <v-card-text>
            <scatter-chart v-if="resaAttendibile && graficoResa.datasets" style="height: 400px;" :data="graficoResa"
              :options="opzioniScatter" :key="keyGrafico" />
            <div v-else-if="dati && !resaAttendibile" class="text-center text--secondary pa-10">
              Il grafico ore / fatturato non è disponibile con il filtro operatore:
              il fatturato non è attribuibile al singolo operatore.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Segnali di rischio -->
      <v-col cols="12" md="5">
        <v-card class="elevation-2 fill-height">
          <v-card-title>Segnali di rischio</v-card-title>
          <v-card-text class="pb-0 text-caption text--secondary">
            Clicca su un segnale per filtrare la tabella dei clienti a rischio.
          </v-card-text>
          <v-list dense>
            <v-list-item v-for="tipo in tipiSegnale" :key="tipo.codice" @click="selezionaSegnale(tipo.codice)"
              :class="{ 'v-list-item--active primary--text': filtroSegnale === tipo.codice }">
              <v-list-item-icon class="mr-3">
                <v-icon :color="tipo.conteggio > 0 ? tipo.colore : 'grey'">{{ tipo.icona }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ tipo.titolo }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ tipo.descrizione }}
                  <span v-if="tipo.richiedeFatturato && !resaAttendibile" class="warning--text">
                    (non disponibile con il filtro operatore)
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip small :color="tipo.conteggio > 0 ? tipo.colore : ''" :dark="tipo.conteggio > 0">
                  {{ tipo.conteggio }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- Clienti a rischio -->
    <v-row class="ma-0 pa-0 mt-3">
      <v-col cols="12">
        <v-card class="elevation-2">
          <v-card-title>
            Clienti a rischio
            <v-chip small color="error" dark class="ml-3">{{ clientiRischio.length }} clienti</v-chip>
            <v-chip v-if="filtroSegnale" small close class="ml-2" @click:close="filtroSegnale = null">
              {{ titoloSegnale(filtroSegnale) }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <v-data-table :headers="intestazioniRischio" :items="clientiRischio" :items-per-page="10" dense
              sort-by="punteggio" sort-desc mobile-breakpoint="0" class="elevation-1 tabella-cliccabile"
              no-data-text="Nessun cliente a rischio" @click:row="apriDettaglio">
              <template v-slot:item.punteggio="{ item }">
                <v-chip small dark :color="coloreRischio(item.rischio)">{{ item.rischio }}</v-chip>
              </template>
              <template v-slot:item.segnali="{ item }">
                <v-tooltip bottom v-for="s in item.segnali" :key="s.codice" max-width="360">
                  <template v-slot:activator="{ on }">
                    <v-chip x-small outlined class="mr-1 my-1" :color="coloreGravita(s.gravita)" v-on="on">
                      {{ s.titolo }}
                    </v-chip>
                  </template>
                  <span>{{ s.dettaglio }}</span>
                </v-tooltip>
              </template>
              <template v-slot:item.ore="{ item }">
                <div class="text-no-wrap">{{ fmtOre(item.ore) }}</div>
                <div class="text-caption text-no-wrap" :class="classeVariazione(item.varOre)">
                  {{ item.orePrec > 0 ? fmtPerc(item.varOre) + ' vs ' + fmtOre(item.orePrec) : '-' }}
                </div>
              </template>
              <template v-slot:item.fatturato="{ item }">
                <div class="text-no-wrap">{{ fmtFatturato(item.fatturato) }}</div>
                <div class="text-caption text-no-wrap" :class="classeVariazione(item.varFatturato)">
                  {{ resaAttendibile && item.fatturatoPrec > 0 ? fmtPerc(item.varFatturato) + ' vs ' +
                    fmtEuro(item.fatturatoPrec) : '-' }}
                </div>
              </template>
              <template v-slot:item.resa="{ item }">
                <span class="text-no-wrap">{{ item.resa == null ? '-' : fmtEuro(item.resa, 2) }}</span>
              </template>
              <template v-slot:item.ultimaAttivita="{ item }">
                <span v-if="item.ultimaAttivita" class="text-no-wrap">
                  {{ fmtData(item.ultimaAttivita) }}
                  <span class="text-caption"
                    :class="item.giorniDaUltimaAttivita > dati.soglie.giorniInattivita ? 'error--text' : 'text--secondary'">
                    ({{ item.giorniDaUltimaAttivita }} gg)
                  </span>
                </span>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Resa per cliente -->
    <v-row class="ma-0 pa-0 mt-3">
      <v-col cols="12">
        <v-card class="elevation-2">
          <v-card-title>
            Resa per cliente
            <v-spacer />
            <v-switch v-model="mostraSenzaOre" label="Mostra anche i clienti senza ore" dense hide-details
              class="mt-0 mr-4" />
            <v-text-field v-model="ricerca" append-icon="search" label="Cerca cliente" single-line hide-details dense
              style="max-width: 280px;" />
          </v-card-title>
          <v-card-text>
            <v-data-table :headers="intestazioniResa" :items="clientiTabella" :search="ricerca" height="480px"
              fixed-header dense disable-pagination hide-default-footer mobile-breakpoint="0"
              sort-by="ore" sort-desc class="elevation-1 tabella-cliccabile" no-data-text="Nessun dato disponibile"
              no-results-text="Nessun cliente trovato" @click:row="apriDettaglio">
              <template v-slot:item.cliente="{ item }">
                <span class="font-weight-medium">{{ item.cliente }}</span>
              </template>
              <template v-slot:item.ore="{ item }"><span class="text-no-wrap">{{ fmtOre(item.ore) }}</span></template>
              <template v-slot:item.varOre="{ item }">
                <span class="text-no-wrap" :class="classeVariazione(item.varOre)">{{ item.orePrec > 0 ?
                  fmtPerc(item.varOre) : '-' }}</span>
              </template>
              <template v-slot:item.orePrec="{ item }"><span class="text-no-wrap">{{ fmtOre(item.orePrec) }}</span>
              </template>
              <template v-slot:item.percFatturabili="{ item }">
                <span class="text-no-wrap">{{ item.percFatturabili == null ? '-' : fmtNum(item.percFatturabili, 0) + '%'
                  }}</span>
              </template>
              <template v-slot:item.tariffaMedia="{ item }">
                <span class="text-no-wrap">{{ item.tariffaMedia == null ? '-' : fmtEuro(item.tariffaMedia, 2) }}</span>
              </template>
              <template v-slot:item.fatturato="{ item }"><span class="text-no-wrap">{{ fmtFatturato(item.fatturato)
                  }}</span></template>
              <template v-slot:item.varFatturato="{ item }">
                <span class="text-no-wrap" :class="classeVariazione(item.varFatturato)">{{ resaAttendibile &&
                  item.fatturatoPrec > 0 ? fmtPerc(item.varFatturato) : '-' }}</span>
              </template>
              <template v-slot:item.resa="{ item }">
                <span class="text-no-wrap" :class="classeResa(item)">{{ item.resa == null ? '-' : fmtEuro(item.resa, 2)
                  }}</span>
              </template>
              <template v-slot:item.resaPrec="{ item }">
                <span class="text-no-wrap">{{ item.resaPrec == null ? '-' : fmtEuro(item.resaPrec, 2) }}</span>
              </template>
              <template v-slot:item.realizzo="{ item }">
                <span class="text-no-wrap">{{ item.realizzo == null ? '-' : fmtNum(item.realizzo, 0) + '%' }}</span>
              </template>
              <template v-slot:item.punteggio="{ item }">
                <v-chip v-if="item.rischio !== 'nessuno'" x-small dark :color="coloreRischio(item.rischio)">
                  {{ item.rischio }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dettaglio cliente -->
    <v-dialog v-model="dialogDettaglio" max-width="1000px" :fullscreen="$functions.isMobile()" scrollable>
      <v-card v-if="clienteSel">
        <v-card-title>
          {{ clienteSel.cliente }}
          <v-chip v-if="clienteSel.rischio !== 'nessuno'" small dark class="ml-3"
            :color="coloreRischio(clienteSel.rischio)">
            rischio {{ clienteSel.rischio }}
          </v-chip>
          <v-spacer />
          <v-btn icon @click="dialogDettaglio = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <v-row dense>
            <v-col cols="6" md="3" v-for="m in metricheCliente" :key="m.etichetta">
              <div class="text-caption text--secondary">{{ m.etichetta }}</div>
              <div class="text-h6">{{ m.valore }}</div>
              <div class="text-caption" :class="classeVariazione(m.variazione)">
                {{ m.variazione === undefined ? m.nota : fmtPerc(m.variazione) + ' vs ' + annoPrec }}
              </div>
            </v-col>
          </v-row>

          <v-alert v-for="s in clienteSel.segnali" :key="s.codice" dense text class="mt-3 mb-0"
            :color="coloreGravita(s.gravita)" icon="warning">
            <strong>{{ s.titolo }}.</strong> {{ s.dettaglio }}
          </v-alert>

          <v-progress-linear v-if="caricamentoDettaglio" indeterminate class="mt-3" />

          <div v-if="graficoDettaglio.datasets" class="mt-4">
            <mixed-chart style="height: 320px;" :data="graficoDettaglio" :options="opzioniDettaglio"
              :key="keyDettaglio" />
          </div>

          <div v-if="operatoriCliente.length" class="mt-4">
            <div class="text-subtitle-2 mb-1">Chi ha dedicato le ore ({{ periodoCorrente }})</div>
            <v-simple-table dense>
              <template v-slot:default>
                <tbody>
                  <tr v-for="o in operatoriCliente" :key="o.nome">
                    <td>{{ o.nome }}</td>
                    <td class="text-right">{{ fmtOre(o.ore) }}</td>
                    <td class="text-right text--secondary">{{ fmtNum(o.ore / clienteSel.ore * 100, 0) }}%</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { get_resa_clienti, get_dettaglio_cliente_ore } from "@/api/api.clienti.js";
import ScatterChart from "@/components/chart/ScatterChart.js";
import MixedChart from "@/components/chart/MixedChart.js";
import mixinCharts from "@/components/chart/mixinCharts.js";
import mixinRichieste from "./mixinRichieste.js";

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default {
  name: "TabClientiResa",

  components: {
    ScatterChart,
    MixedChart
  },

  mixins: [mixinCharts, mixinRichieste],

  props: {
    filtri: { type: Object, default: () => ({}) },
    aggiornaDati: { type: Number, default: 0 }
  },

  data: () => ({
    caricamento: false,
    dati: null,

    // Filtri con cui sono stati caricati i dati a video: il dettaglio di un cliente usa gli stessi
    filtriCaricati: {},

    graficoResa: {},
    opzioniScatter: {},
    keyGrafico: 0,

    filtroSegnale: null,
    ricerca: '',
    mostraSenzaOre: false,

    dialogDettaglio: false,
    clienteSel: null,
    caricamentoDettaglio: false,
    graficoDettaglio: {},
    opzioniDettaglio: {},
    keyDettaglio: 0,
    operatoriCliente: []
  }),

  computed: {
    resaAttendibile() {
      return !this.dati || this.dati.resaAttendibile;
    },

    annoPrec() {
      return (this.dati ? this.dati.anno : this.filtri.anno) - 1;
    },

    totali() {
      return this.dati ? this.dati.totali : {
        ore: 0, orePrec: 0, oreFatturabili: 0, fatturato: 0, fatturatoPrec: 0,
        resa: 0, resaPrec: 0, oreResaBassa: 0, clientiARischio: 0, clientiRischioAlto: 0
      };
    },

    clienti() {
      return this.dati ? this.dati.clienti : [];
    },

    periodoCorrente() {
      return this.dati ? `${this.fmtData(this.dati.da)} - ${this.fmtData(this.dati.a)}` : '';
    },

    periodoPrecedente() {
      return this.dati ? `${this.fmtData(this.dati.daPrec)} - ${this.fmtData(this.dati.aPrec)}` : '';
    },

    schede() {
      const t = this.totali;
      const variazione = (corrente, precedente) => precedente > 0 ? Math.round((corrente - precedente) / precedente * 1000) / 10 : null;
      const resaOk = this.resaAttendibile && t.resa > 0;

      return [
        { etichetta: 'Ore dedicate', valore: this.fmtOre(t.ore), variazione: variazione(t.ore, t.orePrec) },
        this.resaAttendibile
          ? { etichetta: 'Fatturato', valore: this.fmtEuro(t.fatturato), variazione: variazione(t.fatturato, t.fatturatoPrec) }
          : { etichetta: 'Fatturato', valore: '-', nota: 'non attribuibile all\'operatore' },
        {
          etichetta: 'Resa media',
          valore: resaOk ? this.fmtEuro(t.resa, 2) + '/h' : '-',
          variazione: resaOk ? variazione(t.resa, t.resaPrec) : null
        },
        {
          etichetta: 'Ore fatturabili',
          valore: t.ore > 0 ? this.fmtNum(t.oreFatturabili / t.ore * 100, 0) + '%' : '-',
          nota: `${this.fmtOre(t.oreFatturabili)} su ${this.fmtOre(t.ore)}`
        },
        {
          etichetta: 'Ore a bassa resa',
          valore: this.resaAttendibile ? this.fmtOre(t.oreResaBassa) : '-',
          nota: this.resaAttendibile && t.ore > 0 ? `${this.fmtNum(t.oreResaBassa / t.ore * 100, 0)}% delle ore` : ''
        },
        {
          etichetta: 'Clienti a rischio',
          valore: String(t.clientiARischio),
          nota: `${t.clientiRischioAlto} ad alto rischio`
        }
      ];
    },

    tipiSegnale() {
      if (!this.dati) return [];
      const s = this.dati.soglie;
      const conta = codice => this.clienti.filter(c => c.segnali.some(x => x.codice === codice)).length;

      return [
        { codice: 'NESSUNA_ORA', titolo: 'Nessuna ora', icona: 'event_busy', colore: 'error', descrizione: `Nessuna ora nel periodo, ma almeno ${s.oreMinime} h nello stesso periodo dell'anno precedente` },
        { codice: 'ORE_IN_CALO', titolo: 'Ore in calo', icona: 'trending_down', colore: 'orange', descrizione: `Ore in calo di oltre il ${s.caloPercentuale}% rispetto all'anno precedente` },
        { codice: 'INATTIVO', titolo: 'Inattivo', icona: 'pause_circle_outline', colore: 'orange', descrizione: `Nessuna ora registrata da più di ${s.giorniInattivita} giorni` },
        { codice: 'RESA_BASSA', richiedeFatturato: true, titolo: 'Resa bassa', icona: 'money_off', colore: 'orange', descrizione: `Resa oraria sotto il ${s.resaBassaPercentuale}% della media (almeno ${s.oreMinime} h dedicate)` },
        { codice: 'RESA_IN_CALO', richiedeFatturato: true, titolo: 'Resa in calo', icona: 'trending_down', colore: 'amber', descrizione: `Resa oraria in calo di oltre il ${s.caloPercentuale}% rispetto all'anno precedente` },
        { codice: 'FATTURATO_IN_CALO', richiedeFatturato: true, titolo: 'Fatturato in calo', icona: 'euro_symbol', colore: 'orange', descrizione: `Fatturato in calo di oltre il ${s.caloPercentuale}% (anno precedente di almeno € ${s.fatturatoMinimo})` },
        { codice: 'ORE_NON_FATTURATE', richiedeFatturato: true, titolo: 'Ore non fatturate', icona: 'receipt', colore: 'orange', descrizione: `Fatturato sotto il ${s.realizzoPercentuale}% del valore delle ore fatturabili (almeno € ${s.valorizzazioneMinima})` }
      ].map(t => Object.assign(t, { conteggio: conta(t.codice) }));
    },

    clientiRischio() {
      return this.clienti.filter(c => c.rischio !== 'nessuno'
        && (!this.filtroSegnale || c.segnali.some(s => s.codice === this.filtroSegnale)));
    },

    clientiTabella() {
      return this.mostraSenzaOre ? this.clienti : this.clienti.filter(c => c.ore > 0 || c.orePrec > 0);
    },

    intestazioniRischio() {
      return [
        { text: 'Cliente', value: 'cliente' },
        { text: 'Rischio', value: 'punteggio', align: 'center' },
        { text: 'Segnali', value: 'segnali', sortable: false },
        { text: 'Ore', value: 'ore', align: 'end' },
        { text: 'Fatturato', value: 'fatturato', align: 'end' },
        { text: 'Resa €/h', value: 'resa', align: 'end' },
        { text: 'Ultima attività', value: 'ultimaAttivita', align: 'end' }
      ];
    },

    intestazioniResa() {
      return [
        { text: 'Cliente', value: 'cliente' },
        { text: 'Ore', value: 'ore', align: 'end' },
        { text: 'Var. ore', value: 'varOre', align: 'end' },
        { text: `Ore ${this.annoPrec}`, value: 'orePrec', align: 'end' },
        { text: '% fatturabili', value: 'percFatturabili', align: 'end' },
        { text: 'Tariffa media', value: 'tariffaMedia', align: 'end' },
        { text: 'Fatturato', value: 'fatturato', align: 'end' },
        { text: 'Var. fatt.', value: 'varFatturato', align: 'end' },
        { text: 'Resa €/h', value: 'resa', align: 'end' },
        { text: `Resa ${this.annoPrec}`, value: 'resaPrec', align: 'end' },
        { text: 'Realizzo', value: 'realizzo', align: 'end' },
        { text: 'Rischio', value: 'punteggio', align: 'center' }
      ];
    },

    metricheCliente() {
      const c = this.clienteSel;
      if (!c) return [];
      return [
        { etichetta: 'Ore dedicate', valore: this.fmtOre(c.ore), variazione: c.varOre == null ? undefined : c.varOre, nota: c.orePrec > 0 ? '' : 'nessuna ora l\'anno scorso' },
        this.resaAttendibile
          ? { etichetta: 'Fatturato', valore: this.fmtEuro(c.fatturato), variazione: c.varFatturato == null ? undefined : c.varFatturato, nota: c.fatturatoPrec > 0 ? '' : 'nessuna fattura l\'anno scorso' }
          : { etichetta: 'Fatturato', valore: '-', nota: 'non attribuibile all\'operatore' },
        { etichetta: 'Resa oraria', valore: c.resa == null ? '-' : this.fmtEuro(c.resa, 2) + '/h', variazione: c.varResa == null ? undefined : c.varResa, nota: this.resaAttendibile ? 'media: ' + this.fmtEuro(this.totali.resa, 2) + '/h' : '' },
        { etichetta: 'Ore fatturabili', valore: c.percFatturabili == null ? '-' : this.fmtNum(c.percFatturabili, 0) + '%', nota: c.tariffaMedia == null ? 'nessuna tariffa' : 'tariffa media ' + this.fmtEuro(c.tariffaMedia, 2) + '/h' }
      ];
    }
  },

  methods: {
    async carica() {
      const attuale = this.nuovaRichiesta('resa');
      // I filtri vengono copiati subito: dati e dettaglio dei clienti si riferiscono sempre a questi
      const filtri = Object.assign({}, this.filtri);
      this.caricamento = true;
      try {
        const res = await get_resa_clienti(filtri);
        if (!attuale()) return;

        this.filtriCaricati = filtri;
        this.dati = res.data;
        this.costruisciGrafico();
      } catch (error) {
        console.error("Errore caricamento resa clienti:", error);
      } finally {
        // il caricamento termina solo se questa è ancora la richiesta più recente
        if (attuale()) this.caricamento = false;
      }
    },

    costruisciGrafico() {
      const conOre = this.clienti.filter(c => c.ore > 0);
      const punto = c => ({ x: c.ore, y: c.fatturato, cliente: c.cliente, cod: c.codAnagrafica, resa: c.resa });
      const gruppi = [
        { etichetta: 'Nessun segnale', colore: 'rgba(76, 175, 80, 0.75)', filtro: c => c.rischio === 'nessuno' },
        { etichetta: 'Rischio basso o medio', colore: 'rgba(255, 152, 0, 0.8)', filtro: c => c.rischio === 'basso' || c.rischio === 'medio' },
        { etichetta: 'Rischio alto', colore: 'rgba(183, 28, 28, 0.85)', filtro: c => c.rischio === 'alto' }
      ];

      const datasets = gruppi.map(g => ({
        label: g.etichetta,
        data: conOre.filter(g.filtro).map(punto),
        backgroundColor: g.colore,
        borderColor: g.colore,
        pointRadius: 6,
        pointHoverRadius: 9,
        showLine: false
      }));

      // Linea della resa media: i clienti sopra la linea rendono più della media
      if (this.resaAttendibile && this.totali.resa > 0 && conOre.length) {
        const maxOre = Math.max(...conOre.map(c => c.ore));
        datasets.push({
          label: `Resa media (${this.fmtEuro(this.totali.resa, 2)}/h)`,
          data: [{ x: 0, y: 0 }, { x: maxOre, y: maxOre * this.totali.resa }],
          showLine: true,
          fill: false,
          borderColor: 'rgba(120, 120, 120, 0.8)',
          borderDash: [6, 4],
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          tipo: 'media'
        });
      }

      this.opzioniScatter = this.creaOpzioniScatter();
      this.graficoResa = { datasets };
      this.keyGrafico++;
    },

    creaOpzioniScatter() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        legend: { position: 'bottom' },
        plugins: { datalabels: { display: false } },
        scales: {
          xAxes: [{
            type: 'linear',
            position: 'bottom',
            ticks: { beginAtZero: true },
            scaleLabel: { display: true, labelString: 'Ore dedicate' }
          }],
          yAxes: [{
            ticks: { beginAtZero: true, callback: v => this.fmtEuro(v) },
            scaleLabel: { display: true, labelString: 'Fatturato' }
          }]
        },
        tooltips: {
          filter: (item, data) => !data.datasets[item.datasetIndex].tipo,
          callbacks: {
            title: () => '',
            label: (item, data) => {
              const p = data.datasets[item.datasetIndex].data[item.index];
              const resa = p.resa == null ? '' : ` · ${this.fmtEuro(p.resa, 2)}/h`;
              return [p.cliente, `${this.fmtOre(p.x)} · ${this.fmtEuro(p.y)}${resa}`];
            }
          }
        },
        onClick: (evento, elementi) => {
          if (!elementi || !elementi.length) return;
          const el = elementi[0];
          const p = this.graficoResa.datasets[el._datasetIndex].data[el._index];
          const cliente = p && p.cod ? this.clienti.find(c => c.codAnagrafica === p.cod) : null;
          if (cliente) this.apriDettaglio(cliente);
        }
      };
    },

    async apriDettaglio(cliente) {
      this.clienteSel = cliente;
      this.dialogDettaglio = true;
      this.graficoDettaglio = {};
      this.operatoriCliente = [];
      this.caricamentoDettaglio = true;

      const attuale = this.nuovaRichiesta('dettaglio');
      try {
        // Stessi filtri con cui è stata caricata la tabella, così il dettaglio coincide con la riga cliccata
        const filtri = Object.assign({}, this.filtriCaricati, { cliente: cliente.codAnagrafica });
        const res = await get_dettaglio_cliente_ore(filtri);
        // Se nel frattempo è stato scelto un altro cliente, ignoro la risposta
        if (!attuale()) return;

        const d = res.data;
        const anno = d.anno;
        this.opzioniDettaglio = this.getOptionsMixedChart();
        this.opzioniDettaglio.plugins.datalabels.display = false;
        const datasets = [
          { label: `Ore ${anno}`, data: d.ore, type: 'line', fill: false, borderColor: 'rgba(33, 150, 243, 1)', backgroundColor: 'rgba(33, 150, 243, 1)', borderWidth: 3, pointRadius: 4, yAxisID: 'y-ore' },
          { label: `Ore ${anno - 1}`, data: d.orePrec, type: 'line', fill: false, borderColor: 'rgba(33, 150, 243, 0.45)', backgroundColor: 'rgba(33, 150, 243, 0.45)', borderDash: [5, 5], borderWidth: 2, pointRadius: 3, yAxisID: 'y-ore' }
        ];

        // Con il filtro operatore il fatturato non è attribuibile: si mostrano solo le ore
        if (d.fatturatoDisponibile) {
          datasets.unshift(
            { label: `Fatturato ${anno}`, data: d.fatturato, type: 'bar', backgroundColor: 'rgba(76, 175, 80, 0.7)', borderColor: 'rgba(76, 175, 80, 1)', borderWidth: 1, yAxisID: 'y-importi' },
            { label: `Fatturato ${anno - 1}`, data: d.fatturatoPrec, type: 'line', fill: false, borderColor: 'rgba(255, 152, 0, 0.9)', backgroundColor: 'rgba(255, 152, 0, 0.9)', borderDash: [5, 5], borderWidth: 2, pointRadius: 3, yAxisID: 'y-importi' }
          );
        }

        this.graficoDettaglio = { labels: MESI, datasets };
        this.operatoriCliente = d.operatori.filter(o => o.ore > 0);
        this.keyDettaglio++;
      } catch (error) {
        console.error("Errore caricamento dettaglio cliente:", error);
      } finally {
        this.caricamentoDettaglio = false;
      }
    },

    selezionaSegnale(codice) {
      this.filtroSegnale = this.filtroSegnale === codice ? null : codice;
    },

    titoloSegnale(codice) {
      const tipo = this.tipiSegnale.find(t => t.codice === codice);
      return tipo ? tipo.titolo : codice;
    },

    coloreRischio(rischio) {
      return { alto: 'error', medio: 'orange', basso: 'amber' }[rischio] || 'grey';
    },

    coloreGravita(gravita) {
      return gravita >= 3 ? 'error' : gravita === 2 ? 'orange' : 'amber darken-2';
    },

    // Resa sotto il 50% della media evidenziata in rosso, sopra la media in verde
    classeResa(cliente) {
      if (!this.resaAttendibile || cliente.resa == null || !(this.totali.resa > 0)) return '';
      if (cliente.resa < this.totali.resa * 0.5) return 'error--text';
      if (cliente.resa >= this.totali.resa) return 'success--text';
      return '';
    },

    classeVariazione(valore) {
      if (valore == null || valore === 0) return 'text--secondary';
      return valore > 0 ? 'success--text' : 'error--text';
    },

    iconaVariazione(valore) {
      if (valore == null || valore === 0) return 'trending_flat';
      return valore > 0 ? 'trending_up' : 'trending_down';
    },

    fmtNum(valore, decimali) {
      if (valore == null || isNaN(valore)) return '-';
      return this.$functions.numberWithCommas(Number(valore).toFixed(decimali));
    },

    fmtOre(valore) {
      if (valore == null || isNaN(valore)) return '-';
      // un decimale solo se serve
      return this.$functions.numberWithCommas(String(Math.round(valore * 10) / 10)) + ' h';
    },

    fmtEuro(valore, decimali = 0) {
      if (valore == null || isNaN(valore)) return '-';
      return '€ ' + this.$functions.numberWithCommas(Number(valore).toFixed(decimali));
    },

    // Il fatturato non è attribuibile al singolo operatore: con quel filtro non si mostra
    fmtFatturato(valore) {
      return this.resaAttendibile ? this.fmtEuro(valore) : '-';
    },

    fmtPerc(valore) {
      if (valore == null) return 'n/d';
      return (valore > 0 ? '+' : '') + this.$functions.numberWithCommas(String(valore)) + '%';
    },

    fmtData(iso) {
      return iso ? iso.substring(0, 10).split('-').reverse().join('/') : '';
    }
  },

  mounted() {
    this.carica();
  },

  watch: {
    aggiornaDati() {
      this.carica();
    }
  }
}
</script>

<style>
.tabella-cliccabile tbody tr {
  cursor: pointer;
}
</style>
